import React, { useEffect, useState } from 'react';
import { CheckCircle, AlertCircle, RotateCw, Clock } from 'lucide-react';
import logiwaOrderService from '../services/logiwaOrderService';

const DEFERRED_PREFIX = '[deferred] ';

/**
 * Detail panel section showing the Logiwa push state for a truck.
 * Fetches the link on mount; offers per-call retry buttons when an error is recorded.
 *
 * Renders nothing if the truck has no Logiwa link row (non-LTL / no match).
 *
 * Props:
 *   truckId   number
 */
const LogiwaPushSection = ({ truckId }) => {
  const [link, setLink] = useState(null);
  const [loading, setLoading] = useState(true);
  const [retrying, setRetrying] = useState(null); // 'update' | 'document' | 'ship' | 'vas' | 'all' | null
  const [retryMsg, setRetryMsg] = useState('');

  const reload = async () => {
    setLoading(true);
    try {
      const res = await logiwaOrderService.getLogiwaLink(truckId);
      setLink(res && res.link ? res.link : null);
    } catch (_) {
      setLink(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    reload();
  }, [truckId]); // eslint-disable-line react-hooks/exhaustive-deps

  const doRetry = async (call) => {
    setRetrying(call);
    setRetryMsg('');
    try {
      const res = await logiwaOrderService.retryLogiwaPush(truckId, call);
      const errs = res && res.logiwa && res.logiwa.errors ? res.logiwa.errors : [];
      if (errs.length === 0) {
        setRetryMsg('Push succeeded.');
      } else {
        setRetryMsg('Some calls still failed: ' + errs.join('; '));
      }
      await reload();
    } catch (err) {
      setRetryMsg('Retry failed: ' + (err.message || 'unknown error'));
    } finally {
      setRetrying(null);
    }
  };

  if (loading) {
    return (
      <div className="mt-4 p-3 bg-slate-900/40 rounded-lg border border-slate-700 text-sm text-slate-400">
        Loading Logiwa state…
      </div>
    );
  }
  if (!link) return null;

  const updateOk = !!link.logiwa_update_pushed_at && !link.logiwa_update_error;
  const documentOk = !!link.logiwa_document_pushed_at && !link.logiwa_document_error;
  const shipOk = !!link.logiwa_ship_pushed_at && !link.logiwa_ship_error;
  const shipDeferred = !!(link.logiwa_ship_error && link.logiwa_ship_error.startsWith(DEFERRED_PREFIX));
  const vasOk = !!link.logiwa_vas_pushed_at && !link.logiwa_vas_error;

  return (
    <div className="mt-4 p-3 bg-slate-900/40 rounded-lg border border-slate-700">
      <div className="flex items-center justify-between mb-2">
        <h5 className="text-sm font-semibold text-slate-200">Logiwa Push</h5>
        <span className="text-xs text-slate-500">Order: {link.logiwa_shipment_order_code}</span>
      </div>

      <div className="grid grid-cols-2 gap-2 text-xs text-slate-300">
        <div>
          <div className="text-slate-500">Cost</div>
          <div>{link.total_shipping_cost != null ? `${link.total_shipping_cost} ${link.total_shipping_cost_currency || 'USD'}` : '—'}</div>
        </div>
        <div>
          <div className="text-slate-500">Tracking</div>
          <div>{link.tracking_number || '—'}</div>
        </div>
        <div>
          <div className="text-slate-500">BOL Number</div>
          <div>{link.bol_number || '—'}</div>
        </div>
        <div>
          <div className="text-slate-500">Logiwa identifier</div>
          <div className="font-mono text-[11px] break-all">{link.logiwa_shipment_order_identifier}</div>
        </div>
        {link.logiwa_vas_quantity_sent != null && (
          <div>
            <div className="text-slate-500">Billed to invoice</div>
            <div>${Number(link.logiwa_vas_quantity_sent).toFixed(2)}</div>
          </div>
        )}
      </div>

      <div className="mt-3 space-y-1 text-xs">
        <CallStatus
          label="Order update (cost / tracking / BOL number)"
          ok={updateOk}
          pushedAt={link.logiwa_update_pushed_at}
          error={link.logiwa_update_error}
        />
        <CallStatus
          label="BOL document upload"
          ok={documentOk}
          pushedAt={link.logiwa_document_pushed_at}
          error={link.logiwa_document_error}
        />
        <CallStatus
          label="Order shipped (close in Logiwa)"
          ok={shipOk}
          deferred={shipDeferred}
          pushedAt={link.logiwa_ship_pushed_at}
          error={link.logiwa_ship_error}
        />
        <CallStatus
          label="Billing charge posted (VAS)"
          ok={vasOk}
          pushedAt={link.logiwa_vas_pushed_at}
          error={link.logiwa_vas_error}
        />
      </div>

      {(link.logiwa_update_error || link.logiwa_document_error || link.logiwa_ship_error || link.logiwa_vas_error) && (
        <div className="mt-3 flex items-center gap-2 flex-wrap">
          {link.logiwa_update_error && (
            <button
              onClick={() => doRetry('update')}
              disabled={retrying !== null}
              className="text-xs px-2 py-1 bg-blue-700 hover:bg-blue-600 text-white rounded disabled:opacity-50 inline-flex items-center"
            >
              <RotateCw className={`w-3 h-3 mr-1 ${retrying === 'update' ? 'animate-spin' : ''}`} />
              Retry update
            </button>
          )}
          {link.logiwa_document_error && (
            <button
              onClick={() => doRetry('document')}
              disabled={retrying !== null}
              className="text-xs px-2 py-1 bg-blue-700 hover:bg-blue-600 text-white rounded disabled:opacity-50 inline-flex items-center"
            >
              <RotateCw className={`w-3 h-3 mr-1 ${retrying === 'document' ? 'animate-spin' : ''}`} />
              Retry document
            </button>
          )}
          {link.logiwa_ship_error && (
            <button
              onClick={() => doRetry('ship')}
              disabled={retrying !== null}
              className="text-xs px-2 py-1 bg-blue-700 hover:bg-blue-600 text-white rounded disabled:opacity-50 inline-flex items-center"
            >
              <RotateCw className={`w-3 h-3 mr-1 ${retrying === 'ship' ? 'animate-spin' : ''}`} />
              Retry ship
            </button>
          )}
          {link.logiwa_vas_error && !link.logiwa_vas_pushed_at && (
            <button
              onClick={() => doRetry('vas')}
              disabled={retrying !== null}
              className="text-xs px-2 py-1 bg-blue-700 hover:bg-blue-600 text-white rounded disabled:opacity-50 inline-flex items-center"
            >
              <RotateCw className={`w-3 h-3 mr-1 ${retrying === 'vas' ? 'animate-spin' : ''}`} />
              Retry billing
            </button>
          )}
          <button
            onClick={() => doRetry('all')}
            disabled={retrying !== null}
            className="text-xs px-2 py-1 bg-slate-700 hover:bg-slate-600 text-slate-200 rounded disabled:opacity-50 inline-flex items-center"
          >
            <RotateCw className={`w-3 h-3 mr-1 ${retrying === 'all' ? 'animate-spin' : ''}`} />
            Retry all
          </button>
        </div>
      )}

      {retryMsg && (
        <div className="mt-2 text-xs text-slate-400">{retryMsg}</div>
      )}
    </div>
  );
};

const CallStatus = ({ label, ok, deferred, pushedAt, error }) => {
  const displayError = deferred && error ? error.slice(DEFERRED_PREFIX.length) : error;
  let icon;
  if (ok) {
    icon = <CheckCircle className="w-3.5 h-3.5 text-green-400 mr-1 mt-0.5 flex-shrink-0" />;
  } else if (deferred) {
    icon = <Clock className="w-3.5 h-3.5 text-amber-400 mr-1 mt-0.5 flex-shrink-0" />;
  } else {
    icon = <AlertCircle className={`w-3.5 h-3.5 ${error ? 'text-red-400' : 'text-slate-500'} mr-1 mt-0.5 flex-shrink-0`} />;
  }
  return (
    <div className="flex items-start">
      {icon}
      <div>
        <div className="text-slate-300">{label}</div>
        {ok && pushedAt && (
          <div className="text-slate-500">Pushed {new Date(pushedAt).toLocaleString()}</div>
        )}
        {error && (
          <div className={`${deferred ? 'text-amber-300' : 'text-red-300'} break-words`}>{displayError}</div>
        )}
        {!ok && !error && (
          <div className="text-slate-500">Not yet pushed</div>
        )}
      </div>
    </div>
  );
};

export default LogiwaPushSection;
